import React, { useEffect, useMemo, useState } from "react";
import {
  ActionKey,
  ActionLabel,
  DeleteLabel,
  editLabel,
  NewLabel,
  NoLabel,
  StatusLabel,
  StatusUpdateMessageLabel,
  YesLabel,
} from "Components/constants/Role";
import TableContainer from "Components/Base/BaseTable";
import BaseButton from "Components/Base/BaseButton";
import {
  Admin,
  checkStatusCodeSuccess,
  NormalDate,
  notFound,
  rupeeSymbol,
  Search,
} from "Components/constants/Common";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { RiSearchLine } from "react-icons/ri";
import { WorkOrderKey, WorkOrderLabel } from "Components/constants/WorkOrder";
import { editWorkOrderStatusApi, listOfWorkOrderApi } from "Api/workOrder";
import Loader from "Components/Base/BaseLoader";
import BaseModal from "Components/Base/BaseModal";
import { getItem } from "Components/constants/SessionStores";

const WorkOrder = () => {
  document.title = WorkOrderLabel.WorkOrderTitle;
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [workOrder, setWorkOrder] = useState<any[]>([]);
  const [searchWorkOrder, setSearchWorkOrder] = useState<
    Array<any>
  >([]);
  const [totalNumberOfRows, setTotalNumberOfRows] = useState(0);
  const [columnName, setColumnName] = useState("id");
  const [sortOrder, setSortOrder] = useState("desc");
  const [loader, setLoader] = useState<boolean>(false);
  const [editModal, setEditModal] = useState<boolean>(false);
  const [workOrderId, setWorkOrderId] = useState<number | null>(null);
  const [submitLoader, setSubmitLoader] = useState<boolean>(false);
  const [quotationId, setQuotationId] = useState<number | null>(null);

  const navigate = useNavigate();
  const fetchWorkOrder = async () => {
    const payload = {
      pageSize,
      page: currentPage,
      sortKey: columnName,
      sortValue: sortOrder,
    };
    setLoader(true);
    listOfWorkOrderApi(payload)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          setWorkOrder(res?.data?.workOrderList || []);
          setTotalRecords(res?.data?.totalItems || 0);
          setTotalPages(res?.data?.totalPages);
          setCurrentPage(res?.data?.currentPage);
          setTotalNumberOfRows(res?.data?.numberOfRows);
          toast.success(res?.message);
        } else {
          setWorkOrder([]);
        }
      })
      .catch(() => {
        setWorkOrder([]);
      })
      .finally(() => setLoader(false));
  };

  const fetchSearchWorkOrderData = () => {
    const payload = {
      pageSize,
      page: currentPage,
      search: searchValue,
      sortKey: columnName,
      sortValue: sortOrder,
    };
    setLoader(true);
    listOfWorkOrderApi(payload)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          setSearchWorkOrder(res?.data?.workOrderList || []);
          setTotalRecords(res?.data?.totalItems || 0);
          setTotalPages(res?.data?.totalPages);
          setCurrentPage(res?.data?.currentPage);
          setTotalNumberOfRows(res?.data?.numberOfRows);
          toast.success(res?.message);
        } else {
          setSearchWorkOrder([]);
        }
      })
      .catch(() => {
        setSearchWorkOrder([]);
      })
      .finally(() => setLoader(false));
  };

  useEffect(() => {
    if (searchValue) {
      fetchSearchWorkOrderData();
    } else {
      fetchWorkOrder();
    }
  }, [currentPage, pageSize, searchValue, sortOrder, columnName]);

  const workOrderColumns = useMemo(
    () => [
      {
        header: WorkOrderLabel.VendorId,
        accessorKey: WorkOrderKey.VendorID,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: WorkOrderLabel.VendorName,
        accessorKey: WorkOrderKey.VendorName,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: WorkOrderLabel.QuoteNo,
        accessorKey: WorkOrderKey.QuoteNo,
        enableSorting: true,
        enableColumnFilter: false,
        cell: (cell: any) => {
          return cell?.row?.original?.quotation.quotation_number;
        },
      },
      {
        header: WorkOrderLabel.QuoteAmt,
        accessorKey: WorkOrderKey.QuoteAmt,
        enableSorting: true,
        enableColumnFilter: false,
        cell: (cell: any) => {
            return rupeeSymbol(cell?.row?.original?.quotation_incl_tax_amount);
        },
      },
      {
        header: WorkOrderLabel.WorkOrderNo,
        accessorKey: WorkOrderKey.WorkOrderNo,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: WorkOrderLabel.WorkOrderAmt,
        accessorKey: WorkOrderKey.WorkOrderAmt,
        enableSorting: true,
        enableColumnFilter: false,
        cell: (cell: any) => {
            return rupeeSymbol(cell?.row?.original?.work_order_incl_tax_amount);
        },
      },
      {
        header: WorkOrderLabel.WorkOrderDate,
        accessorKey: WorkOrderKey.WorkOrderDate,
        enableSorting: true,
        enableColumnFilter: false,
        cell: (cell: any) => {
          return (
            <div className="list-group bg-none list-group-flush p-1">
              <span>{NormalDate(cell.row?.original?.work_order_date)}</span>
            </div>
          );
        },
      },
      {
        header: WorkOrderLabel.QuotationDate,
        accessorKey: WorkOrderKey.QuotationDate,
        enableSorting: true,
        enableColumnFilter: false,
        cell: (cell: any) => {
          return (
            <div className="list-group bg-none list-group-flush p-1">
              <span>{NormalDate(cell.row?.original?.quotation_date)}</span>
            </div>
          );
        },
      },
      {
        header: WorkOrderLabel.TaxInvoice,
        accessorKey: WorkOrderKey.TaxInvoice,
        enableSorting: true,
        enableColumnFilter: false,
        cell: (cell: any) => {
          const generated = cell.row?.original?.is_tax_invoice_generated;
          return (
            <div className="list-group bg-none list-group-flush p-1">
              <span className={`badge ${generated === true ? "bg-success" : "bg-info"}`}>
                  {generated ? WorkOrderLabel.Generated : WorkOrderLabel.Pending}
                </span>
            </div>
          );
        },
      },
      {
        header: ActionLabel,
        accessorKey: ActionKey,
        cell: (cell: any) => {
          return (
            <div className="d-flex justify-content-start enable-action flex-nowrap gap-2 icon">
              <span className="tooltip-container">
                <i
                  className={`mdi mdi-eye-outline fs-4 text-warning cursor-pointer`}
                  onClick={() => {
                    navigate(`/view-work-order/${cell?.row?.original?.id}`, {
                      state: { viewWorkOrder: true },
                    });
                  }}
                ></i>
                <span className="tooltip-text">{WorkOrderLabel.View}</span>
              </span>
              <span className="tooltip-container">
                <i
                  className={`mdi mdi-pencil-outline fs-4 text-info cursor-pointer ${
                    cell?.row?.original?.status === WorkOrderLabel.Publish &&
                    `icon-disabled`
                  }`}
                  onClick={() =>
                    navigate(`/edit-work-order/${cell?.row?.original?.id}`)
                  }
                ></i>
                <span className="tooltip-text">{editLabel}</span>
              </span>
          {getItem("role") === Admin && (
            <>
              <span className="tooltip-container">
                <i
                  className={`mdi mdi-check fs-4 cursor-pointer text-success ${cell?.row?.original?.status === WorkOrderLabel.Publish &&
                    `icon-disabled`
                  }`}
                  onClick={() =>
                    handleStatusModal(
                      cell?.row?.original?.id,
                      cell?.row?.original?.quotation_id
                    )
                  }
                ></i>
                <span className="tooltip-text">{WorkOrderLabel.Publish}</span>
              </span>
            </>
          )}
            </div>
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
      },
    ],
    []
  );

  const handleStatusModal = (id: number, quoteId: number) => {
    setWorkOrderId(id);
    setEditModal(true);
    setQuotationId(quoteId);
  };

  const fetchData = (page: number) => {
    setCurrentPage(page);
  };

  const fetchSortingData = (page: number, id: string, order: string) => {
    setCurrentPage(page);
    setColumnName(id);
    setSortOrder(order);
  };

  const handleSearch = (value: string) => {
    if (value !== searchValue) {
      setCurrentPage(1);
      setSearchValue(value);
    }
  };

  const handleEditStatus = () => {
      setSubmitLoader(true);
      const payload = {
        quotation_id: quotationId,
      };
      editWorkOrderStatusApi(Number(workOrderId), payload)
        .then((res) => {
          if (checkStatusCodeSuccess(res?.statusCode)) {
            fetchWorkOrder();
            toast.success(res?.message);
          } else {
            toast.error(res?.message);
          }
        })
        .catch((err) => {
          toast.error(err?.response?.data?.message || err?.message);
        })
        .finally(() => {
          setSubmitLoader(false);
          setEditModal(false);
        });
    };
  return (
    <div className="page-content">
      <div className="flex-grow-1 mx-3">
        <div className="mb-1 mx-2 d-flex justify-content-between align-items-center">
          <span className="fs-4 d-flex gap-4">{WorkOrderLabel.WorkOrder} </span>
          <div className="d-flex align-items-center gap-2">
            <BaseButton
              className="btn btn-success"
              onClick={() => navigate("/add-work-order")}
              children={
                <>
                  <i className="mdi mdi-plus" /> {NewLabel}
                </>
              }
            />
          </div>
        </div>
        <div className="card py-2 px-4 overflow-x-auto">
          {loader || workOrder?.length > 0 ? (
            <TableContainer
              customPageSize={pageSize}
              setCustomPageSize={setPageSize}
              isGlobalFilter={true}
              columns={workOrderColumns}
              data={searchValue ? searchWorkOrder : workOrder || []}
              tableClass="table bg-white"
              manualPagination={true}
              manualFiltering={true}
              tableLoader={loader}
              hasManualPagination={true}
              SearchPlaceholder={Search}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPages={totalPages}
              totalRecords={totalRecords}
              onSearch={handleSearch}
              totalNumberOfRows={totalNumberOfRows}
              fetchData={fetchData}
              fetchSortingData={fetchSortingData}
            />
          ) : (
            <div className="py-4 text-center">
              <div>
                <span className="fs-1 text-success">
                  <RiSearchLine />
                </span>
              </div>
              <div className="mt-4">
                <h5>{notFound.dataNotFound}</h5>
                <p className="text-muted">{notFound.thereIsNoDataFound}</p>
              </div>
            </div>
          )}
        </div>
        <BaseModal
          open={editModal}
          toggle={() => setEditModal(!editModal)}
          submit={() => handleEditStatus()}
          title={StatusLabel}
          closeLabel={NoLabel}
          submitLabel={YesLabel}
          closeLabelColor="danger"
          submitLabelColor="success"
          loader={submitLoader}
          size="sm"
        >
          <div>{StatusUpdateMessageLabel}</div>
        </BaseModal>
      </div>
    </div>
  );
};

export default WorkOrder;
